import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FeedRefreshBanner from './FeedRefreshBanner';
import { useShowcaseFeedLoader } from '../hooks/useShowcaseFeedLoader';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  fetchShowcaseP2PBets, 
  clearError,
  selectShowcaseP2PBets,
  selectP2PShowcaseLoading,
  selectP2PShowcaseError,
  selectP2PShowcasePagination
} from '../redux/slices/p2pShowcaseSlice';
import { 
  Users, 
  Calendar, 
  ArrowRight,
  DollarSign,
  Target,
  TrendingUp,
  UserCheck,
  Clock
} from 'lucide-react';
import ShowcaseNavigation from './ShowcaseNavigation';
import { formatAppDate, formatAppDateTime } from '../app/utils/appTimeFormat';

const P2PCard = ({ p2pBet, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'settled':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'voided':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency: 'CASH',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatAppDate(dateString);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return formatAppDateTime(dateString);
  };

  const formatOutcome = (agreedOdds) => {
    if (!agreedOdds) return 'N/A';
    return `${agreedOdds.section} - ${agreedOdds.subSection}`;
  };

  const getParticipantsCount = (p2pBet) => {
    const joinedCount = p2pBet.peopleJoined?.length || 0;
    const acceptedInvites = p2pBet.invites?.filter(invite => invite.status === 'Accepted').length || 0;
    return joinedCount + acceptedInvites;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="relative group"
    >
      <div 
        className="relative bg-gradient-to-br from-gray-900/80 to-black/90 backdrop-blur-sm 
                   border border-gray-700/50 rounded-2xl p-6 h-full
                   hover:border-blue-500/50 transition-all duration-300
                   hover:shadow-2xl hover:shadow-blue-500/10"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >

        {/* Fixture Header with Logos */}
        <div className="mb-6">
          {/* Team vs Team with Logos */}
          <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                {p2pBet.fixture?.teams?.home?.logo ? (
                  <img 
                    src={p2pBet.fixture.teams.home.logo} 
                    alt={p2pBet.fixture.teams.home.name}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-xs font-bold" style={{display: p2pBet.fixture?.teams?.home?.logo ? 'none' : 'flex'}}>
                  {p2pBet.fixture?.teams?.home?.name?.charAt(0) || 'A'}
                </div>
              </div>
              <span className="text-sm font-medium text-white truncate">
                {p2pBet.fixture?.teams?.home?.name || 'Team A'}
              </span>
            </div>
            
            <div className={`text-xs flex-shrink-0 px-1.5 py-0.5 rounded-full ${
              p2pBet.fixture?.status?.short === 'NS' 
                ? 'bg-green-500/20 text-green-400' 
                : p2pBet.fixture?.status?.short === 'FT'
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-yellow-500/20 text-yellow-400'
            }`}>
              {p2pBet.fixture?.status?.short || 'NS'}
            </div>
            
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-sm font-medium text-white truncate">
                {p2pBet.fixture?.teams?.away?.name || 'Team B'}
              </span>
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                {p2pBet.fixture?.teams?.away?.logo ? (
                  <img 
                    src={p2pBet.fixture.teams.away.logo} 
                    alt={p2pBet.fixture.teams.away.name}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-xs font-bold" style={{display: p2pBet.fixture?.teams?.away?.logo ? 'none' : 'flex'}}>
                  {p2pBet.fixture?.teams?.away?.name?.charAt(0) || 'B'}
                </div>
              </div>
            </div>
          </div>

          {/* Fixture Date and Time */}
          <div className="text-center mb-2">
            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
              <Calendar className="w-4 h-4" />
              <span>{formatDateTime(p2pBet.fixture?.date)}</span>
            </div>
          </div>
        </div>

        {/* P2P Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-400" />
            <div>
              <p className="text-xs text-gray-500">Creator budget</p>
              <p className="text-sm font-semibold text-white">
                {formatAmount(p2pBet.creatorBudget ?? p2pBet.totalBetAmount ?? p2pBet.amount)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-purple-400" />
            <div>
              <p className="text-xs text-gray-500">Counters</p>
              <p className="text-sm font-semibold text-white">
                {getParticipantsCount(p2pBet)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-orange-400" />
            <div>
              <p className="text-xs text-gray-500">Min stake</p>
              <p className="text-sm font-semibold text-white">
                {formatAmount(p2pBet.amount || 500)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <div>
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm font-semibold text-white">
                {formatDate(p2pBet.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Selected Outcome */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Selected Outcome
          </p>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span className="text-gray-300">
                  {formatOutcome(p2pBet.agreedOdds)}
                </span>
              </div>
              {p2pBet.agreedOdds?.key && (
                <span className="text-gray-500 text-xs">
                  {p2pBet.agreedOdds.key}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Call to Action Overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-2xl flex items-center justify-center"
            >
              <div className="flex gap-3">
                <motion.button
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 hover:from-blue-700 hover:to-blue-800 transition-all duration-300"
                >
                  <span>iOS</span>
                  <ArrowRight className="w-3 h-3" />
                </motion.button>
                <motion.button
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 hover:from-green-700 hover:to-green-800 transition-all duration-300"
                >
                  <span>Android</span>
                  <ArrowRight className="w-3 h-3" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gradient Border Effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </motion.div>
  );
};

const P2PShowcase = () => {
  const dispatch = useDispatch();
  const p2pBets = useSelector(selectShowcaseP2PBets);
  const isLoading = useSelector(selectP2PShowcaseLoading);
  const error = useSelector(selectP2PShowcaseError);
  const pagination = useSelector(selectP2PShowcasePagination);
  const { load, isUpdating, refreshFailed } = useShowcaseFeedLoader({
    fetchAction: fetchShowcaseP2PBets,
    items: p2pBets,
    pageSize: 9,
  });

  const loadMore = () => {
    dispatch(fetchShowcaseP2PBets({ 
      page: pagination.page + 1, 
      limit: 9 
    }));
  };

  if (error && p2pBets.length === 0) {
    return (
      <>
        <ShowcaseNavigation title="P2P Showcase" icon={Users} />
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-20">
          <div className="container mx-auto px-4 text-center">
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-6 max-w-md mx-auto">
              <p className="text-red-400 mb-4">Failed to load P2P betting data</p>
              <button
                onClick={() => {
                  dispatch(clearError());
                  dispatch(fetchShowcaseP2PBets({ page: 1, limit: 9, force: true }));
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ShowcaseNavigation title="P2P Showcase" icon={Users} />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 pb-20">
        <div className="container mx-auto px-4">
        <FeedRefreshBanner
          isUpdating={isUpdating}
          refreshFailed={refreshFailed}
          onRetry={() => load({ force: true })}
        />

        {/* Loading State */}
        {isLoading && p2pBets.length === 0 && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* P2P Bets Grid */}
        {p2pBets.length > 0 && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
            >
              <AnimatePresence>
                {p2pBets.map((p2pBet, index) => (
                  <P2PCard key={p2pBet._id} p2pBet={p2pBet} index={index} />
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Load More Button */}
            {pagination.hasNextPage && (
              <div className="text-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={loadMore}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <span>Load More P2P Bets</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && p2pBets.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">No P2P bets found</h3>
            <p className="text-gray-400 mb-6">
              No P2P betting opportunities available at the moment.
            </p>
          </motion.div>
        )}

        </div>
      </div>
    </>
  );
};

export default P2PShowcase;
